import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  type ElearningEnrollment 
} from '../../services/elearningOrderService';
import { getStudentEnrollments } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useToast } from '../../hooks/useToast';
import { getDomainFilterParams } from '../../hooks/useDomainQuery';
import StudentLayout from '../../components/student/StudentLayout';
import { 
  BookOpen, 
  Clock, 
  TrendingUp,
  Users,
  Award,
  Play,
  CheckCircle,
  BarChart3,
  Calendar,
  Target,
  Filter,
  Search,
  X,
  Brain,
  Bell,
  Trophy,
  ShoppingBag,
  Menu,
  Sun,
  Moon,
  Star,
  Zap,
  ArrowRight,
  ChevronRight,
  Plus,
  Edit,
  Eye,
  Download,
  Share,
  Heart,
  Bookmark,
  MessageCircle,
  ThumbsUp,
  MoreHorizontal,
  TrendingUpIcon,
  Activity,
  BookmarkIcon,
  GraduationCap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import LearningStatsChart from '../../components/student/dashboard/LearningStatsChart';
import QuizFeedbackSystem from '../../components/student/quiz/QuizFeedbackSystem';
import NotesAndBookmarks from '../../components/student/tools/NotesAndBookmarks';
import StudyPlannerCalendar from '../../components/student/planning/StudyPlannerCalendar';
import SmartReviewSystem from '../../components/student/tools/SmartReviewSystem';
import StudyReminders from '../../components/student/notifications/StudyReminders';
import StudentOrdersSection from '../../components/student/dashboard/StudentOrdersSection';
import StudentSidebar from '../../components/student/dashboard/StudentSidebar';
import ModernDashboardStats from '../../components/student/dashboard/ModernDashboardStats';
import ContinueLearning from '../../components/student/dashboard/ContinueLearning';
import RecommendedCourses from '../../components/student/dashboard/RecommendedCourses';
import { 
  generateAccessToken,
  getCustomerProfileByToken 
} from '../../services/customerProfileService';
import { supabase } from '../../lib/supabase';
import { PublicFooter } from '../../components/layout/PublicFooter';

export const StudentDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    if (profile?.id) {
      loadEnrollments(profile.id);
    }
  };

  const loadEnrollments = async (studentId: string) => {
    const { tenantId } = getDomainFilterParams();
    if (!studentId || !tenantId) return;

    setIsLoading(true);
    try {
      const enrollmentsData = await getStudentEnrollments(studentId, tenantId);
      setEnrollments(enrollmentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger vos cours',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'En cours', variant: 'default' as const, color: 'bg-blue-500/20 text-blue-400' },
      completed: { label: 'Terminé', variant: 'default' as const, color: 'bg-green-500/20 text-green-400' },
      suspended: { label: 'Suspendu', variant: 'destructive' as const, color: 'bg-red-500/20 text-red-400' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDuration = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours === 1) return '1h';
    return `${hours}h`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Statistiques calculées
  const stats = {
    totalCourses: enrollments.length,
    activeCourses: enrollments.filter(e => e.status === 'active').length,
    completedCourses: enrollments.filter(e => e.status === 'completed').length,
    averageProgress: enrollments.length > 0 
      ? Math.round(enrollments.reduce((sum, e) => sum + e.progress_percentage, 0) / enrollments.length)
      : 0
  };





  return (
    <StudentLayout
      title="Tableau de bord"
      description={`Bienvenue ${studentProfile?.first_name || ''} ! Continuez votre apprentissage.`}
      onStudentProfileLoaded={handleStudentProfileLoaded}
    >
      <SEOMetaTags 
        title="Mon Tableau de Bord - E-Learning"
        description="Suivez votre progression dans vos cours e-learning et accédez à vos formations."
        keywords="tableau de bord, cours, progression, e-learning"
      />

      {/* Statistiques */}
      <ModernDashboardStats stats={stats} enrollments={enrollments} />

      {/* Contenu principal en grille */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-8">
          {/* Continuer l'apprentissage */}
          <ContinueLearning enrollments={enrollments} />
          
          {/* Graphique des statistiques */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-purple-500" />
                <span>Progression d'apprentissage</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LearningStatsChart 
                studentId={studentProfile?.id || ""}
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar droite */}
        <div className="space-y-8">
          {/* Cours recommandés */}
          <RecommendedCourses enrollments={enrollments} />
          
          {/* Rappels d'étude */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5 text-orange-500" />
                <span>Rappels</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StudyReminders studentId={studentProfile?.id || ""} />
            </CardContent>
          </Card>
        </div>
      </div>
    </StudentLayout>
  );
};

export default StudentDashboardPage;
